/**
 * #44 Yazışma Denetimi — sohbet detayı (salt okunur).
 *
 * DetailShell `modeless` ile kullanılır: bu bir kayıt formu DEĞİL, bir okuma ekranıdır.
 * Kaydet/Sil aksiyonu YOKTUR — denetim kaydı düzeltilebilir olursa denetim olmaktan çıkar;
 * mesaj silme işi de elle değil saklama politikasıyla (TeamChatRetentionJob) yapılır.
 *
 * Akışta gösterilen metin MASKELENMİŞ hâlidir (mesaj tablosu ham metin taşımaz). Maskelenmiş
 * mesajlarda yakalanan kelimeler etiket olarak görünür; ham metin İhlaller listesindedir.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { DetailShell, Section } from './detail/DetailShell';

const API_BASE = '/api/teamchatarchive';
const LIST_ROUTE = '/team-chat-archive';

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR');
}

export function TeamChatArchiveDetailPage() {
  const { id } = useParams();
  usePageMeta('Yazışma Detayı', 'Ekip içi mesajlaşma arşivi.');

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/messages?take=500`, { headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(payload?.message || payload?.Message || `Yazışma alınamadı (${res.status})`);
      }
      setDetail(payload?.data ?? payload?.Data ?? payload);
    } catch (e) {
      showToast('error', e.message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const isGroup = detail?.kind === 'TEAM_GROUP';
  const title = detail?.title || (isGroup ? 'Ekip grubu' : 'Birebir sohbet');

  return (
    <DetailShell
      modeless
      loading={loading}
      crumb={[
        { label: 'Yazışma Denetimi', to: LIST_ROUTE },
        { label: title, current: true },
      ]}
    >
      <div className="dps-form">
        <Section
          icon="user"
          title="Sohbet"
          subtitle={isGroup ? 'Ekip grubu — yönetici ve doğrudan astları' : 'Birebir sohbet'}
        >
          <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
            <div><b>Açılış:</b> {fmtDateTime(detail?.createdDate)}</div>
            <div><b>Son mesaj:</b> {fmtDateTime(detail?.lastMessageDate)}</div>
            <div><b>Mesaj sayısı:</b> {detail?.messages?.length ?? 0}
              {detail?.messages?.length === 500 && ' (son 500 gösteriliyor)'}
            </div>
          </div>
        </Section>

        <Section icon="user" title="Katılımcılar" subtitle="Okuma/yazma zamanlarıyla">
          {(detail?.participants ?? []).length === 0 ? (
            <div style={mutedStyle}>Katılımcı kaydı yok.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {detail.participants.map((p) => (
                <div key={p.userId} style={participantStyle}>
                  <div style={{ fontWeight: 600 }}>
                    {p.fullName}
                    {p.sicilId && <span style={mutedInlineStyle}> · {p.sicilId}</span>}
                  </div>
                  <div style={mutedInlineStyle}>
                    Son yazma: {fmtDateTime(p.lastSentDate)} · Son okuma: {fmtDateTime(p.lastReadDate)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section
          icon="list"
          title="Yazışma"
          subtitle="Maskelenmiş metin — kullanıcının yazdığı ham metin İhlaller listesindedir"
        >
          {(detail?.messages ?? []).length === 0 ? (
            <div style={mutedStyle}>Bu sohbette mesaj yok (saklama süresi geçmiş olabilir).</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {detail.messages.map((m) => (
                <div key={m.id} style={m.isMasked ? messageMaskedStyle : messageStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{m.senderName}</span>
                    <span style={mutedInlineStyle}>{fmtDateTime(m.createdDate)}</span>
                  </div>
                  <div style={{ marginTop: 3 }}>{m.body}</div>
                  {m.isMasked && (
                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--danger,#c62828)' }}>
                      Yasaklı kelime maskelendi{m.matchedWords ? `: ${m.matchedWords}` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Toast toast={toast} />
    </DetailShell>
  );
}

const mutedStyle = { fontSize: 13, color: 'var(--text-secondary,#6b7280)' };
const mutedInlineStyle = { fontSize: 12, color: 'var(--text-secondary,#6b7280)', fontWeight: 400 };
const participantStyle = {
  padding: '8px 10px',
  borderRadius: 10,
  border: '1px solid var(--border-color,#e5e7eb)',
  fontSize: 13,
};
const messageStyle = {
  padding: '8px 10px',
  borderRadius: 10,
  border: '1px solid var(--border-color,#e5e7eb)',
  background: 'var(--bg-card,#fff)',
  fontSize: 13,
};
const messageMaskedStyle = {
  ...messageStyle,
  borderColor: 'var(--danger,#c62828)',
  background: 'rgba(198,40,40,0.04)',
};
